/**
 * UseCreditsModal — utilisation Crédits Mytenue (programme fidélité L.521-1)
 *
 * v1.12.848 — Phase 5
 *   - Liste dynamique : tous les abos (TIERS) + tous les add-ons (ADDONS)
 *   - Sections groupées : Abonnements / Try-Ons / AI Scans / Wardrobe
 *   - Filtrage par tier courant (eligibleTiers / tier actuel)
 *   - Déduction automatique appliquée au prix Stripe (web) ou bonus IAP (mobile)
 *
 * IMMUTABLE (compliance L.521-1) :
 *   - Aucun retrait monétaire
 *   - FIFO strict côté backend (/credits/use)
 *   - Usage interne Mytenue uniquement
 */
import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, ActivityIndicator, Alert, StyleSheet, Modal, Platform } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { getCreditBalance, useCredits, redeemCredit, ADDON_TO_REWARD } from '../services/MytenueCredits';
import { TIERS } from '../services/PremiumService';
import { ADDONS } from '../config/addonsConfig';

const GOLD = '#D4AF72';
const DARK = '#1A1A1A';
const isWeb = Platform.OS === 'web';

// Construit dynamiquement la liste complète des items payables (abos + add-ons)
function buildOptions(currentTier = 'free') {
  const t = String(currentTier || 'free').toLowerCase();

  // ─── Abonnements (basic / premium / gold × monthly / yearly) ───
  const subs = [];
  ['basic', 'premium', 'gold'].forEach(tier => {
    const tierDef = TIERS[tier];
    if (!tierDef) return;
    // Upgrade ou renouvellement même tier autorisé
    // Downgrade pas via crédits (passe par /stripe/change-subscription)
    if (tier !== 'free') {
      subs.push({
        id: `${tier}_monthly`,
        section: 'subscription',
        label: `${tierDef.label || tier.charAt(0).toUpperCase() + tier.slice(1)} · Mensuel`,
        sublabel: tier === t ? 'Renouveler' : `Passer à ${tier.charAt(0).toUpperCase() + tier.slice(1)}`,
        price: Number(tierDef.priceMonthly || 0),
        type: tier === t ? 'subscription_renewal' : 'tier_upgrade',
        meta: { tier, period: 'monthly' },
      });
      // Annuel volontairement masqué ici : l'échange par Crédit Mytenue ne porte
      // que sur l'abonnement MENSUEL (comp 1 mois). On n'affiche pas les abos annuels.
    }
  });

  // ─── Add-ons (filtrés par eligibleTiers du user) ───
  const addons = Object.values(ADDONS)
    .filter(a => a.eligibleTiers.includes(t))
    .map(a => ({
      id: a.id,
      section: a.type === 'tryons' ? 'tryons'
            : a.type === 'scans' ? 'scans'
            : 'wardrobe',
      label: a.labelFr,
      sublabel: a.descFr,
      price: Number(a.priceEur || 0),
      type: 'addon_purchase',
      meta: { addonId: a.id, addonType: a.type },
      badge: a.badge === 'popular' ? 'Populaire' : a.badge === 'best_value' ? 'Meilleur prix' : null,
    }));

  return [...subs, ...addons];
}

const SECTION_LABELS = {
  subscription: 'Abonnements',
  tryons: 'Try-Ons',
  scans: 'AI Scans',
  wardrobe: 'Wardrobe',
};

// Phase 2 — abo 1 mois offert par crédit (comp serveur, comptes Free, mensuel)
const SUB_REWARD = {
  basic: 'redeem_sub_basic_1m',
  premium: 'redeem_sub_premium_1m',
  gold: 'redeem_sub_gold_1m',
};

export default function UseCreditsModal({ visible, onClose, onUsed, currentTier = 'free' }) {
  const { user, getValidToken } = useAuth();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const options = useMemo(() => buildOptions(currentTier), [currentTier]);
  const grouped = useMemo(() => {
    const g = {};
    options.forEach(o => { (g[o.section] = g[o.section] || []).push(o); });
    return g;
  }, [options]);

  useEffect(() => {
    if (!visible) return;
    setSelected(null);
    (async () => {
      setLoading(true);
      try {
        const token = getValidToken ? await getValidToken() : user?.accessToken;
        const b = await getCreditBalance(token);
        setBalance(b);
      } finally { setLoading(false); }
    })();
  }, [visible]);

  // v1.12.848 — Crédit Mytenue : on N'APPELLE PAS /credits/use ici.
  // C'est le webhook Stripe (checkout.session.completed) qui consomme les
  // crédits APRÈS paiement OK → pas de double-débit si user cancel.
  // Sur mobile (IAP), le revenuecat-webhook appliquera le bonus.
  const handleUse = async () => {
    if (!selected) return;
    const active = Number(balance?.active || 0);
    if (active <= 0) {
      Alert.alert('Aucun crédit actif', 'Vous n\'avez pas de Crédit Mytenue utilisable actuellement.');
      return;
    }
    const amountToUse = Math.min(active, selected.price);
    const reste = Math.max(0, selected.price - amountToUse);
    setSubmitting(true);
    try {
      if (isWeb) {
        // Web : lancer Stripe Checkout avec déduction crédit
        if (selected.section === 'subscription') {
          const { startCheckout } = require('../services/StripeService');
          await startCheckout(selected.meta.tier, selected.meta.period, { useCreditAmountEur: amountToUse });
          // redirige vers Stripe — pas de retour ici
        } else {
          // Add-on
          const { startAddonCheckout } = require('../services/StripeService');
          await startAddonCheckout(selected.meta.addonId, { useCreditAmountEur: amountToUse });
        }
      } else {
        // Mobile : ÉCHANGE INTÉGRAL (100% crédit, AUCUN paiement Apple/Google).
        // Récompense fidélité L.521-1 → octroi serveur direct via /credits/redeem.
        let rewardId, isSub = false, grantLabel = selected.label;
        if (selected.section === 'subscription') {
          // Phase 2 : 1 mois d'abo offert par crédit — MENSUEL + comptes FREE uniquement
          if (selected.meta.period !== 'monthly') {
            Alert.alert('Échange mensuel uniquement', 'Seul l\'abonnement mensuel est échangeable contre du Crédit Mytenue.');
            return;
          }
          if (String(currentTier || 'free').toLowerCase() !== 'free') {
            Alert.alert('Réservé aux comptes Free', 'L\'échange d\'un mois d\'abonnement par crédit est réservé aux comptes gratuits.');
            return;
          }
          rewardId = SUB_REWARD[selected.meta.tier];
          isSub = true;
          grantLabel = `1 mois ${selected.label}`;
        } else {
          rewardId = ADDON_TO_REWARD[selected.meta.addonId];
        }
        if (!rewardId) {
          Alert.alert('Indisponible', 'Cette récompense n\'est pas échangeable par crédit pour le moment.');
          return;
        }
        // Garde-fou compliance : échange 100% crédit → le solde doit couvrir TOUT le prix
        if (active < selected.price) {
          const manque = (selected.price - active).toFixed(2);
          Alert.alert('Crédit insuffisant', `Il vous manque ${manque} € de Crédit Mytenue pour débloquer « ${grantLabel} ».`);
          return;
        }
        Alert.alert(
          '🎁 Échanger mes crédits',
          `Débloquer « ${grantLabel} » avec ${selected.price.toFixed(2)} € de Crédit Mytenue ?\n\n` +
          `Offert grâce à vos achats Mytenue — aucun paiement.`,
          [
            { text: 'Annuler', style: 'cancel' },
            { text: 'Échanger', onPress: async () => {
              setSubmitting(true);
              try {
                const token = getValidToken ? await getValidToken() : user?.accessToken;
                const clientToken = `${rewardId}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
                const result = await redeemCredit(token, rewardId, clientToken);
                // GA4 — tracking des achats par Crédit Mytenue (event dédié, hors revenue réel)
                try {
                  const { trackEvent } = require('../services/AnalyticsService');
                  trackEvent('credit_redemption', {
                    gateway: 'credit_redemption',
                    kind: isSub ? 'subscription_comp' : 'addon',
                    reward_id: rewardId,
                    item_id: isSub ? `sub_${selected.meta.tier}_monthly` : selected.meta.addonId,
                    item_name: grantLabel,
                    tier: isSub ? selected.meta.tier : null,
                    quantity: result?.granted?.quantity || 1,
                    credit_spent: selected.price,
                    value: selected.price,
                    currency: 'EUR',
                    platform: Platform.OS,
                  });
                } catch (_) {}
                // Rafraîchir le solde + prévenir Profil/Dressing (reload, pas de restart)
                try { const b = await getCreditBalance(token); setBalance(b); } catch (_) {}
                if (isSub) {
                  // subscription_changed → AuthContext recharge le tier (premium).
                  // addon_balance_changed → ProfileScreen.onRefresh() re-fetch les compteurs
                  // (try-on, IA, dressing) pour refléter les nouvelles limites du tier
                  // (sinon le compteur try-on reste sur l'ancienne valeur free « X/sem »).
                  try { const E = require('react-native').DeviceEventEmitter; E.emit('subscription_changed', { tier: selected.meta.tier, period: 'monthly', source: 'credit_comp' }); E.emit('addon_balance_changed'); } catch (_) {}
                } else {
                  try { require('react-native').DeviceEventEmitter.emit('addon_balance_changed'); } catch (_) {}
                }
                const q = result?.granted?.quantity;
                Alert.alert('✓ Débloqué', isSub
                  ? `1 mois ${selected.label} activé, offert grâce à vos crédits.`
                  : `${q ? `+${q} ` : ''}${selected.label} ajouté à votre compte, offert grâce à vos crédits.`);
                onUsed && onUsed({ selected, amountUsed: selected.price, remaining: 0, gateway: 'credit_redeem', result });
                onClose && onClose();
              } catch (ie) {
                if (ie?.code === 'insufficient_credit') {
                  Alert.alert('Crédit insuffisant', `Il vous manque ${(Number(ie.required || 0) - Number(ie.active || 0)).toFixed(2)} € pour cette récompense.`);
                } else if (ie?.code === 'already_subscribed') {
                  Alert.alert('Déjà abonné', 'Vous avez déjà un abonnement actif — l\'offre par crédit est réservée aux comptes Free.');
                } else if (ie?.code === 'comp_already_active') {
                  Alert.alert('Déjà actif', 'Vous bénéficiez déjà d\'un mois d\'abonnement offert en cours.');
                } else {
                  Alert.alert('Échange impossible', ie?.message || 'Réessayez plus tard.');
                }
              } finally { setSubmitting(false); }
            }},
          ]
        );
      }
    } catch (e) {
      Alert.alert('Erreur', e.message || 'Impossible de démarrer le paiement.');
    } finally { setSubmitting(false); }
  };

  const renderOption = (opt) => {
    const isSel = selected?.id === opt.id;
    const active = Number(balance?.active || 0);
    const willUse = Math.min(active, opt.price);
    const rest = Math.max(0, opt.price - willUse);

    // ── Mobile : échange intégral (100% crédit). Éligibilité par option ──
    // - Consommable : échangeable si le solde couvre TOUT le prix.
    // - Abonnement (Phase 2) : MENSUEL + compte FREE uniquement (comp serveur).
    const isSubscription = opt.section === 'subscription';
    const isFreeAccount = String(currentTier || 'free').toLowerCase() === 'free';
    let mobileReason = null; // string = grisé avec raison ; null = échangeable si solde suffisant
    if (!isWeb && isSubscription) {
      if (opt.meta?.period !== 'monthly') mobileReason = 'Mensuel uniquement';
      else if (!isFreeAccount) mobileReason = 'Réservé aux comptes Free';
    }
    const affordable = active >= opt.price;
    const disabled = !isWeb && (mobileReason !== null || !affordable);
    const manque = Math.max(0, opt.price - active);

    return (
      <TouchableOpacity
        key={opt.id}
        style={[s.optionRow, isSel && s.optionRowActive, disabled && s.optionRowDisabled]}
        activeOpacity={disabled ? 1 : 0.85}
        onPress={() => { if (!disabled) setSelected(opt); }}
      >
        <View style={{ flex: 1 }}>
          <View style={s.optionHeader}>
            <Text style={[s.optionLabel, disabled && s.optionLabelDisabled]}>{opt.label}</Text>
            {opt.badge && !disabled && <Text style={s.badge}>{opt.badge}</Text>}
          </View>
          {!!opt.sublabel && <Text style={s.optionSublabel} numberOfLines={2}>{opt.sublabel}</Text>}
          <View style={s.priceRow}>
            <Text style={s.optionPrice}>{opt.price.toFixed(2)} €</Text>
            {isWeb ? (
              balance?.active > 0 && willUse > 0 && (
                <Text style={s.optionDiscount}>− {willUse.toFixed(2)} €  →  {rest.toFixed(2)} €</Text>
              )
            ) : mobileReason ? (
              <Text style={s.optionHint}>{mobileReason}</Text>
            ) : affordable ? (
              <Text style={s.optionRedeem}>{isSubscription ? '🎁 1 mois offert' : '🎁 Échanger · offert'}</Text>
            ) : (
              <Text style={s.optionHint}>Encore {manque.toFixed(2)} € à cumuler</Text>
            )}
          </View>
        </View>
        {isSel && <Text style={s.checkmark}>✓</Text>}
      </TouchableOpacity>
    );
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={s.card}>
          <Text style={s.title}>✨ Utiliser mes Crédits Mytenue</Text>
          <TouchableOpacity style={s.close} onPress={onClose}>
            <Text style={s.closeTxt}>×</Text>
          </TouchableOpacity>

          {loading ? (
            <ActivityIndicator color={GOLD} style={{ marginVertical: 30 }} />
          ) : (
            <ScrollView style={{ maxHeight: 540 }} showsVerticalScrollIndicator={false}>
              <View style={s.balanceBox}>
                <Text style={s.balanceLabel}>SOLDE UTILISABLE</Text>
                <Text style={s.balanceValue}>{Number(balance?.active || 0).toFixed(2)} €</Text>
                {balance?.pending > 0 && (
                  <Text style={s.pendingLine}>+ {Number(balance.pending).toFixed(2)} € en attente validation partenaire</Text>
                )}
                {balance?.nearestExpiry && (
                  <Text style={s.expiryLine}>Premier crédit expire le {new Date(balance.nearestExpiry).toLocaleDateString('fr-FR')}</Text>
                )}
              </View>

              {Object.entries(grouped).map(([section, items]) => (
                <View key={section} style={{ marginTop: 14 }}>
                  <Text style={s.sectionLabel}>{SECTION_LABELS[section] || section.toUpperCase()}</Text>
                  {items.map(renderOption)}
                </View>
              ))}

              <Text style={s.disclaimer}>
                Crédits Mytenue : programme de fidélité (art. L.521-1 CMF). Utilisables uniquement
                au sein de Mytenue, non convertibles en argent, non transférables. FIFO strict.
                {isWeb
                  ? ' Sur web : déduction appliquée au paiement Stripe.'
                  : ' Sur mobile : échange intégral en crédit (récompense offerte, sans paiement) dès que votre solde couvre la récompense.'}
              </Text>
            </ScrollView>
          )}

          {selected && (
            <TouchableOpacity
              style={[s.cta, submitting && { opacity: 0.5 }]}
              onPress={handleUse}
              disabled={submitting}
              activeOpacity={0.85}
            >
              {submitting
                ? <ActivityIndicator size="small" color={GOLD} />
                : <Text style={s.ctaTxt}>{isWeb ? 'APPLIQUER MES CRÉDITS' : 'ÉCHANGER MES CRÉDITS'}</Text>}
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );
}

const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'center', padding: 16 },
  card: { backgroundColor: '#FFF', borderRadius: 16, padding: 20, maxHeight: '92%' },
  title: { fontSize: 17, fontWeight: '700', color: DARK, marginBottom: 4 },
  close: { position: 'absolute', top: 14, right: 14, width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
  closeTxt: { fontSize: 22, color: '#999' },
  balanceBox: {
    backgroundColor: '#FBF8F0', borderRadius: 12, padding: 14, marginTop: 12,
    borderWidth: 1, borderColor: '#E8DCC2', alignItems: 'center',
  },
  balanceLabel: { fontSize: 10, color: '#7A6E55', letterSpacing: 1.5, fontWeight: '700' },
  balanceValue: { fontSize: 30, fontWeight: '300', color: GOLD, marginTop: 4 },
  pendingLine: { fontSize: 10, color: '#7A6E55', marginTop: 4, fontStyle: 'italic' },
  expiryLine: { fontSize: 9, color: '#A0936F', marginTop: 2 },
  sectionLabel: { fontSize: 10, fontWeight: '700', color: '#7A6E55', letterSpacing: 1.5, marginBottom: 6, paddingHorizontal: 2 },
  optionRow: {
    flexDirection: 'row', alignItems: 'center', padding: 12, borderRadius: 10,
    borderWidth: 1, borderColor: '#EFE8D8', marginBottom: 6, backgroundColor: '#FAFAF5',
  },
  optionRowActive: { borderColor: GOLD, borderWidth: 2, backgroundColor: '#FFFCF5' },
  optionRowDisabled: { opacity: 0.5 },
  optionLabelDisabled: { color: '#999' },
  optionHint: { fontSize: 10, color: '#A0936F', fontWeight: '600' },
  optionRedeem: { fontSize: 10, color: GOLD, fontWeight: '800', letterSpacing: 0.3 },
  optionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  optionLabel: { fontSize: 13, fontWeight: '600', color: DARK, flex: 1 },
  optionSublabel: { fontSize: 10, color: '#888', marginTop: 2 },
  priceRow: { flexDirection: 'row', alignItems: 'center', marginTop: 6, gap: 8, flexWrap: 'wrap' },
  optionPrice: { fontSize: 12, color: '#444', fontWeight: '600' },
  optionDiscount: { fontSize: 10, color: GOLD, fontWeight: '700' },
  badge: { fontSize: 9, color: GOLD, backgroundColor: '#FFF5E0', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, fontWeight: '700', letterSpacing: 0.5, overflow: 'hidden' },
  checkmark: { fontSize: 20, color: GOLD, fontWeight: '700', marginLeft: 8 },
  disclaimer: { fontSize: 9, color: '#999', textAlign: 'center', marginTop: 16, fontStyle: 'italic', paddingHorizontal: 8, lineHeight: 13 },
  cta: {
    backgroundColor: DARK, paddingVertical: 14, borderRadius: 10, alignItems: 'center',
    marginTop: 12, borderWidth: 1, borderColor: GOLD,
  },
  ctaTxt: { fontSize: 12, fontWeight: '700', color: GOLD, letterSpacing: 1.5 },
});
